import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Save, CheckCircle } from 'lucide-react';

export const ExpenseFormDialog = ({
  open,
  onOpenChange,
  selectedExpense,
  formData,
  setFormData,
  onSubmit,
  categories,
  currencySymbol,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{selectedExpense ? 'Modifier une depense' : 'Ajouter une depense'}</DialogTitle>
        <DialogDescription>
          {selectedExpense ? 'Modifiez les informations de cette depense' : 'Enregistrez une nouvelle depense pour votre exploitation'}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="date">Date *</Label>
            <Input id="date" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required data-testid="expense-date-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="categorie">Categorie *</Label>
            <Select value={formData.categorie_id} onValueChange={(value) => setFormData({ ...formData, categorie_id: value })}>
              <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="Selectionner une categorie" /></SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (<SelectItem key={cat.id} value={cat.id}>{cat.nom}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="montant">Montant * ({currencySymbol})</Label>
            <Input id="montant" type="number" step="0.01" min="0" value={formData.montant} onChange={(e) => setFormData({ ...formData, montant: e.target.value })} required data-testid="expense-montant-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="quantite">Quantite</Label>
            <Input id="quantite" type="number" step="0.1" min="0" value={formData.quantite} onChange={(e) => setFormData({ ...formData, quantite: e.target.value })} placeholder="Ex: 50" data-testid="expense-quantite-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="unite">Unite</Label>
            <Select value={formData.unite} onValueChange={(value) => setFormData({ ...formData, unite: value })}>
              <SelectTrigger data-testid="expense-unite-select"><SelectValue placeholder="Unite" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="kg">Kilogrammes (kg)</SelectItem>
                <SelectItem value="tonnes">Tonnes</SelectItem>
                <SelectItem value="sacs">Sacs</SelectItem>
                <SelectItem value="litres">Litres</SelectItem>
                <SelectItem value="unites">Unites</SelectItem>
                <SelectItem value="heures">Heures</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {categories.find(c => c.id === formData.categorie_id)?.is_alimentation && (
            <div className="col-span-full">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="space-y-0.5">
                  <Label className="text-base font-medium flex items-center gap-2">
                    <span className="text-lg">📦</span>
                    Mettre a jour le stock
                  </Label>
                  <p className="text-sm text-gray-500">
                    Le stock "{categories.find(c => c.id === formData.categorie_id)?.nom}" sera mis a jour ou cree automatiquement
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, update_stock: !formData.update_stock })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.update_stock ? 'bg-blue-500' : 'bg-gray-300'}`}
                  data-testid="expense-update-stock-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.update_stock ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              {formData.update_stock && (
                <div className="flex items-center gap-2 text-blue-600 text-sm mt-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>La quantite sera ajoutee au stock (cree si inexistant)</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} placeholder="Details de la depense..." data-testid="expense-description-input" />
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
          <div className="space-y-0.5">
            <Label htmlFor="paye" className="text-base font-medium">Statut de paiement</Label>
            <p className="text-sm text-gray-500">Indiquez si cette depense a ete reglee</p>
          </div>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, paye: !formData.paye })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.paye ? 'bg-green-500' : 'bg-gray-300'}`}
            data-testid="expense-paye-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.paye ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
        {formData.paye && (
          <div className="flex items-center gap-2 text-green-600 text-sm">
            <CheckCircle className="w-4 h-4" />
            <span>Cette depense sera marquee comme payee</span>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <Button type="submit" className="flex-1 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600" data-testid="expense-submit-button">
            <Save className="w-4 h-4 mr-2" />
            {selectedExpense ? 'Modifier' : 'Ajouter'}
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);
